import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Briefcase, ArrowLeft, Plus, Store, Search } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateJob } from 'features/job/hooks/useJobMutations';
import { createJobSchema } from 'features/job/validations/jobForm';
import { JobStatus, JobVisibility, Currency } from 'features/job/types/job.types';
import { useSellers } from 'features/seller/hooks/useSellers';
import Layout from 'components/ui/Layout';

const CreateJob = () => {
  const navigate = useNavigate();
  const { mutate: createJob, isPending } = useCreateJob();
  const [sellerSearch, setSellerSearch] = useState('');

  // Fetch sellers list for dropdown
  const { data: sellerResponse, isLoading: sellersLoading } = useSellers({
    limit: 100,
    search: sellerSearch,
  });

  const sellers = sellerResponse?.data?.sellers || [];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createJobSchema),
    mode: 'onChange',
    defaultValues: {
      sellerId: '',
      jobTitle: '',
      addressLine1: '',
      addressLine2: '',
      landmark: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      currency: 'INR',
      compensation: '',
      description: '',
      requirement: '',
      applicationDeadline: '',
      applicationDeadlineTime: '11:59 PM',
      applicationLink: '',
      visibility: 'Public',
      status: 'Active',
    },
  });

  const selectedSellerId = watch('sellerId');

  // Handle seller selection and optional autofill of address
  const handleSellerChange = (sellerId) => {
    setValue('sellerId', sellerId, { shouldValidate: true });
    const selected = sellers.find((s) => s._id === sellerId);
    if (selected?.address) {
      if (selected.address.city) setValue('city', selected.address.city);
      if (selected.address.state) setValue('state', selected.address.state);
      if (selected.address.pincode) setValue('pincode', selected.address.pincode);
      if (selected.address.addressLine1) setValue('addressLine1', selected.address.addressLine1);
    }
  };

  const onSubmit = (data) => {
    const payload = {
      sellerId: data.sellerId,
      jobTitle: data.jobTitle,
      currency: data.currency,
      compensation: data.compensation || undefined,
      description: data.description || undefined,
      requirement: data.requirement || undefined,
      applicationDeadline: new Date(data.applicationDeadline).toISOString(),
      applicationDeadlineTime: data.applicationDeadlineTime || undefined,
      applicationLink: data.applicationLink || undefined,
      visibility: data.visibility,
      status: data.status,
      address: {
        addressLine1: data.addressLine1 || undefined,
        addressLine2: data.addressLine2 || undefined,
        landmark: data.landmark || undefined,
        city: data.city || undefined,
        state: data.state || undefined,
        pincode: data.pincode || undefined,
        country: data.country || undefined,
      },
    };

    createJob(payload, {
      onSuccess: () => {
        navigate('/jobs');
      },
    });
  };

  return (
    <Layout>
      <div className='p-6 max-w-6xl mx-auto'>
        {/* Header */}
        <div className='mb-6'>
          <button
            onClick={() => navigate('/jobs')}
            className='flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors'
          >
            <ArrowLeft size={16} />
            Back to Jobs
          </button>
          <h1 className='text-3xl font-bold flex items-center gap-2'>
            <Briefcase className='text-primary' size={32} />
            Post New Job
          </h1>
          <p className='text-muted-foreground mt-1'>
            Create a new job posting on behalf of a registered service provider
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className='bg-card border border-border rounded-lg p-6'>
          <div className='space-y-8'>
            {/* 1. Seller Selection */}
            <div>
              <h2 className='text-xl font-semibold text-foreground mb-4 pb-2 border-b border-border flex items-center gap-2'>
                <Store size={20} className='text-primary' />
                Service Provider / Seller
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>
                    Select Seller <span className='text-destructive'>*</span>
                  </label>
                  <div className='space-y-2'>
                    <select
                      value={selectedSellerId}
                      onChange={(e) => handleSellerChange(e.target.value)}
                      className={`w-full px-4 py-2.5 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                        errors.sellerId ? 'border-destructive' : 'border-border'
                      }`}
                    >
                      <option value=''>-- Choose a Service Provider --</option>
                      {sellers.map((s) => {
                        const name = [s.firstName, s.lastName].filter(Boolean).join(' ') || 'Unnamed Seller';
                        const business = s.restaurantName ? `(${s.restaurantName})` : '';
                        const phone = s.phoneNumber ? ` - ${s.phoneNumber}` : '';
                        return (
                          <option key={s._id} value={s._id}>
                            {name} {business} {phone}
                          </option>
                        );
                      })}
                    </select>
                    {errors.sellerId && (
                      <p className='mt-1.5 text-sm text-destructive'>{errors.sellerId.message}</p>
                    )}
                    {sellersLoading && (
                      <p className='text-xs text-muted-foreground'>Loading sellers...</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Basic Information */}
            <div>
              <h2 className='text-xl font-semibold text-foreground mb-4 pb-2 border-b border-border'>
                Job Information
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Job Title */}
                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>
                    Job Title <span className='text-destructive'>*</span>
                  </label>
                  <input
                    type='text'
                    {...register('jobTitle')}
                    className={`w-full px-4 py-2.5 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                      errors.jobTitle ? 'border-destructive' : 'border-border'
                    }`}
                    placeholder='e.g., Senior AC Technician, Residential Electrician'
                  />
                  {errors.jobTitle && <p className='mt-1.5 text-sm text-destructive'>{errors.jobTitle.message}</p>}
                </div>

                {/* Currency */}
                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>
                    Currency <span className='text-destructive'>*</span>
                  </label>
                  <select
                    {...register('currency')}
                    className={`w-full px-4 py-2.5 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                      errors.currency ? 'border-destructive' : 'border-border'
                    }`}
                  >
                    {Object.values(Currency).map((curr) => (
                      <option key={curr} value={curr}>
                        {curr}
                      </option>
                    ))}
                  </select>
                  {errors.currency && <p className='mt-1.5 text-sm text-destructive'>{errors.currency.message}</p>}
                </div>

                {/* Compensation */}
                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>Compensation / Salary</label>
                  <input
                    type='text'
                    {...register('compensation')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='e.g., ₹25,000 - ₹35,000 / month'
                  />
                </div>

                {/* Description */}
                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>Job Description</label>
                  <textarea
                    {...register('description')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='Describe the job responsibilities, role details...'
                    rows={4}
                  />
                </div>

                {/* Requirements */}
                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>Requirements & Skills</label>
                  <textarea
                    {...register('requirement')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='Experience required, certificates, skills...'
                    rows={4}
                  />
                </div>
              </div>
            </div>

            {/* 3. Location */}
            <div>
              <h2 className='text-xl font-semibold text-foreground mb-4 pb-2 border-b border-border'>
                Job Location
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>Address Line 1</label>
                  <input
                    type='text'
                    {...register('addressLine1')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='Street address, workplace location'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>City</label>
                  <input
                    type='text'
                    {...register('city')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='e.g., Jodhpur, Jaipur'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>State</label>
                  <input
                    type='text'
                    {...register('state')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='e.g., Rajasthan'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>Pincode</label>
                  <input
                    type='text'
                    {...register('pincode')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='e.g., 342001'
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>Country</label>
                  <input
                    type='text'
                    {...register('country')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='India'
                  />
                </div>
              </div>
            </div>

            {/* 4. Application Details */}
            <div>
              <h2 className='text-xl font-semibold text-foreground mb-4 pb-2 border-b border-border'>
                Application Details
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>
                    Application Deadline <span className='text-destructive'>*</span>
                  </label>
                  <input
                    type='date'
                    {...register('applicationDeadline')}
                    className={`w-full px-4 py-2.5 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                      errors.applicationDeadline ? 'border-destructive' : 'border-border'
                    }`}
                  />
                  {errors.applicationDeadline && (
                    <p className='mt-1.5 text-sm text-destructive'>{errors.applicationDeadline.message}</p>
                  )}
                </div>

                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>Deadline Time</label>
                  <input
                    type='text'
                    {...register('applicationDeadlineTime')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                    placeholder='e.g., 11:59 PM'
                  />
                </div>

                <div className='md:col-span-2'>
                  <label className='block text-sm font-medium text-foreground mb-2'>External Application Link (Optional)</label>
                  <input
                    type='url'
                    {...register('applicationLink')}
                    className={`w-full px-4 py-2.5 bg-background border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all ${
                      errors.applicationLink ? 'border-destructive' : 'border-border'
                    }`}
                    placeholder='https://example.com/apply'
                  />
                  {errors.applicationLink && (
                    <p className='mt-1.5 text-sm text-destructive'>{errors.applicationLink.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* 5. Visibility & Status */}
            <div>
              <h2 className='text-xl font-semibold text-foreground mb-4 pb-2 border-b border-border'>
                Visibility & Status
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>
                    Visibility <span className='text-destructive'>*</span>
                  </label>
                  <select
                    {...register('visibility')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                  >
                    {Object.values(JobVisibility).map((vis) => (
                      <option key={vis} value={vis}>
                        {vis}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='block text-sm font-medium text-foreground mb-2'>
                    Status <span className='text-destructive'>*</span>
                  </label>
                  <select
                    {...register('status')}
                    className='w-full px-4 py-2.5 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all'
                  >
                    {Object.values(JobStatus).map((stat) => (
                      <option key={stat} value={stat}>
                        {stat}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className='flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-border'>
            <button
              type='button'
              onClick={() => navigate('/jobs')}
              className='flex-1 px-6 py-3 border border-border rounded-lg text-foreground hover:bg-accent transition-colors font-medium'
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm'
              disabled={isPending}
            >
              {isPending ? (
                <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-primary-foreground' />
              ) : (
                <>
                  <Plus size={18} />
                  <span>Publish Job</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateJob;
